import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { projectsAPI, getMediaUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft,
  Play,
  Pause,
  Download,
  Settings,
  Type,
  Volume2,
  VolumeX,
  Maximize,
  SkipBack,
  SkipForward,
  Loader2,
  Edit3,
  Film
} from 'lucide-react';
import { toast } from 'sonner';

const Preview = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const videoRef = useRef(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [showCaptions, setShowCaptions] = useState(true);
  const [currentCaption, setCurrentCaption] = useState('');
  const [currentClipIndex, setCurrentClipIndex] = useState(0);
  const [videoError, setVideoError] = useState(null);

  // Get video URL - either processed, single video, or first clip
  // Note: processed_video_url might not exist if processing is simulated
  const getVideoUrl = () => {
    if (!project) return null;
    
    // If we have a processed video URL AND it has the correct /api prefix, use it
    // Otherwise fall back to clips (simulated processing doesn't create actual files)
    if (project.processed_video_url && project.processed_video_url.startsWith('/api/')) {
      return project.processed_video_url;
    }
    
    // Legacy single video support
    if (project.video_url) return project.video_url;
    
    // Fall back to clips for multi-clip projects or when processed video doesn't exist
    if (project.clips && project.clips.length > 0) {
      return project.clips[currentClipIndex]?.url;
    }
    return null;
  };

  // Check if we're showing clips instead of processed video
  const isShowingClips = () => {
    if (!project) return false;
    if (project.processed_video_url && project.processed_video_url.startsWith('/api/')) {
      return false;
    }
    return project.clips && project.clips.length > 0;
  };

  const videoUrl = getVideoUrl();
  const showingClips = isShowingClips();

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  // Reset video error when clip changes or video URL changes
  useEffect(() => {
    setVideoError(null);
  }, [currentClipIndex, videoUrl]);

  useEffect(() => {
    // Update current caption based on time
    if (project?.captions && showCaptions) {
      const caption = project.captions.find(
        c => currentTime >= c.start && currentTime <= c.end
      );
      setCurrentCaption(caption?.text || '');
    } else {
      setCurrentCaption('');
    }
  }, [currentTime, project?.captions, showCaptions]);

  const fetchProject = async () => {
    try {
      const response = await projectsAPI.get(projectId);
      setProject(response.data);
    } catch (error) {
      toast.error('Project not found');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = percent * duration;
    }
  };

  const handleSkip = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, Math.min(duration, videoRef.current.currentTime + seconds));
    }
  };

  const handleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
      setMuted(!muted);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const handleDownload = () => {
    const url = videoUrl;
    if (url) {
      const link = document.createElement('a');
      link.href = getMediaUrl(url);
      link.download = `${project.title || 'video'}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Download started');
    }
  };

  const handleNextClip = () => {
    if (project?.clips && currentClipIndex < project.clips.length - 1) {
      setCurrentClipIndex(prev => prev + 1);
    }
  };

  const handlePrevClip = () => {
    if (currentClipIndex > 0) {
      setCurrentClipIndex(prev => prev - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading font-semibold text-2xl text-white mb-4">Project not found</h2>
          <Button onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303]">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="text-white/60 hover:text-white"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading font-semibold text-xl text-white">{project.title}</h1>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
              onClick={() => navigate(`/editor/${projectId}`)}
              data-testid="edit-btn"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600]"
              onClick={handleDownload}
              data-testid="download-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-8 px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Video Player */}
            <div className="lg:col-span-2">
              <Card className="bg-[#0A0A0A] border-white/10 rounded-2xl overflow-hidden">
                {/* Video */}
                <div className="aspect-video bg-black relative group">
                  {videoUrl ? (
                    <>
                      <video
                        ref={videoRef}
                        src={getMediaUrl(videoUrl)}
                        className="w-full h-full object-contain"
                        onTimeUpdate={handleTimeUpdate}
                        onLoadedMetadata={() => {
                          handleLoadedMetadata();
                          setVideoError(null);
                        }}
                        onCanPlay={() => setVideoError(null)}
                        onPlay={() => setPlaying(true)}
                        onPause={() => setPlaying(false)}
                        onEnded={() => setPlaying(false)}
                        onError={(e) => {
                          // Only set error after a brief delay to avoid false positives
                          setTimeout(() => {
                            if (e.target.networkState === 3 && e.target.readyState === 0) {
                              console.error('Video error:', e.target.error);
                              setVideoError('This video format is not supported for browser preview. Please download the file to view it.');
                            }
                          }, 1000);
                        }}
                        playsInline
                        preload="metadata"
                        controls
                      />
                      
                      {/* Video error overlay */}
                      {videoError && (
                        <div className="absolute inset-0 flex items-center justify-center flex-col gap-4 bg-black/80">
                          <div className="text-center px-8">
                            <p className="text-white/80 text-lg mb-2">Video format not supported</p>
                            <p className="text-white/40 text-sm mb-4">{videoError}</p>
                            <Button 
                              onClick={handleDownload} 
                              className="bg-[#CCFF00] text-black hover:bg-[#B3E600]"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download to View
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Play button overlay */}
                      {!playing && !videoError && (
                        <div 
                          className="absolute inset-0 flex items-center justify-center cursor-pointer"
                          onClick={handlePlayPause}
                        >
                          <div className="w-20 h-20 rounded-full bg-[#CCFF00]/20 backdrop-blur-xl flex items-center justify-center hover:bg-[#CCFF00]/30 transition-colors">
                            <Play className="w-8 h-8 text-[#CCFF00] ml-1" />
                          </div>
                        </div>
                      )}

                      {/* Captions overlay */}
                      {currentCaption && !videoError && (
                        <div className="absolute bottom-20 left-0 right-0 text-center pointer-events-none">
                          <span className="bg-black/80 text-white px-4 py-2 rounded-lg text-lg">
                            {currentCaption}
                          </span>
                        </div>
                      )}

                      {/* Clip indicator for multi-clip preview */}
                      {showingClips && project?.clips && project.clips.length > 1 && (
                        <div className="absolute top-4 left-4 bg-black/70 px-3 py-1 rounded-full flex items-center gap-2">
                          <Film className="w-4 h-4 text-[#CCFF00]" />
                          <span className="text-white text-sm">Clip {currentClipIndex + 1} of {project.clips.length}</span>
                        </div>
                      )}
                      
                      {/* Notice when showing clips for completed project */}
                      {showingClips && project?.status === 'completed' && !videoError && (
                        <div className="absolute top-4 right-4 bg-[#CCFF00]/90 px-3 py-1 rounded-full">
                          <span className="text-black text-sm font-medium">Preview Mode</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center flex-col gap-4">
                      <p className="text-white/40">No video available</p>
                      <Button onClick={() => navigate(`/editor/${projectId}`)} variant="outline" className="border-white/20 text-white">
                        Go to Editor
                      </Button>
                    </div>
                  )}
                </div>

                {/* Controls */}
                <div className="p-4 bg-[#121212]">
                  {/* Timeline */}
                  <div className="mb-4">
                    <div 
                      className="h-2 bg-white/10 rounded-full overflow-hidden cursor-pointer"
                      onClick={handleSeek}
                    >
                      <div 
                        className="h-full bg-[#CCFF00] rounded-full transition-all"
                        style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-1 text-xs text-white/40">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>
                  </div>

                  {/* Control buttons */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {showingClips && project?.clips && project.clips.length > 1 ? (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-white/60 hover:text-white"
                            onClick={handlePrevClip}
                            disabled={currentClipIndex === 0}
                          >
                            <SkipBack className="w-5 h-5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-white hover:text-[#CCFF00] w-12 h-12"
                            onClick={handlePlayPause}
                            data-testid="play-pause-btn"
                          >
                            {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-white/60 hover:text-white"
                            onClick={handleNextClip}
                            disabled={currentClipIndex >= project.clips.length - 1}
                          >
                            <SkipForward className="w-5 h-5" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-white/60 hover:text-white"
                            onClick={() => handleSkip(-10)}
                          >
                            <SkipBack className="w-5 h-5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-white hover:text-[#CCFF00] w-12 h-12"
                            onClick={handlePlayPause}
                            data-testid="play-pause-btn"
                          >
                            {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-white/60 hover:text-white"
                            onClick={() => handleSkip(10)}
                          >
                            <SkipForward className="w-5 h-5" />
                          </Button>
                        </>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-white/60 hover:text-white"
                        onClick={handleMute}
                      >
                        {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className={`${showCaptions ? 'text-[#CCFF00]' : 'text-white/60'} hover:text-white`}
                        onClick={() => setShowCaptions(!showCaptions)}
                      >
                        <Type className="w-5 h-5" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-white/60 hover:text-white"
                        onClick={handleFullscreen}
                      >
                        <Maximize className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Side Panel */}
            <div className="space-y-6">
              {/* Project Info */}
              <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
                <h3 className="font-heading font-semibold text-white mb-4">Project Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-white/60">Style</span>
                    <span className="text-white capitalize">{project.video_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Captions</span>
                    <span className="text-white capitalize">{project.caption_style}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Music</span>
                    <span className="text-white capitalize">{project.music_preference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Status</span>
                    <span className={`capitalize ${
                      project.status === 'completed' ? 'text-green-500' :
                      project.status === 'error' ? 'text-red-500' :
                      'text-[#CCFF00]'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                  {project.clips && project.clips.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-white/60">Clips</span>
                      <span className="text-white">{project.clips.length}</span>
                    </div>
                  )}
                </div>
              </Card>

              {/* Clips List - show when viewing clips instead of processed video */}
              {showingClips && project.clips && project.clips.length > 0 && (
                <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
                  <h3 className="font-heading font-semibold text-white mb-4">Clips</h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {project.clips.map((clip, i) => (
                      <div 
                        key={clip.clip_id}
                        className={`p-3 rounded-lg cursor-pointer transition-colors flex items-center gap-3 ${
                          currentClipIndex === i
                            ? 'bg-[#CCFF00]/20 border border-[#CCFF00]/50'
                            : 'bg-[#121212] hover:bg-white/5'
                        }`}
                        onClick={() => setCurrentClipIndex(i)}
                      >
                        <div className={`w-6 h-6 rounded flex items-center justify-center text-sm font-semibold ${
                          currentClipIndex === i ? 'bg-[#CCFF00] text-black' : 'bg-white/10 text-white'
                        }`}>
                          {i + 1}
                        </div>
                        <span className="text-white text-sm truncate flex-1">{clip.filename}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Captions */}
              {project.captions && project.captions.length > 0 && (
                <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
                  <h3 className="font-heading font-semibold text-white mb-4">Captions</h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {project.captions.map((caption, i) => (
                      <div 
                        key={i}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          currentTime >= caption.start && currentTime <= caption.end
                            ? 'bg-[#CCFF00]/20 border border-[#CCFF00]/50'
                            : 'bg-[#121212] hover:bg-white/5'
                        }`}
                        onClick={() => {
                          if (videoRef.current) {
                            videoRef.current.currentTime = caption.start;
                          }
                        }}
                      >
                        <div className="flex justify-between text-xs text-white/40 mb-1">
                          <span>{formatTime(caption.start)}</span>
                          <span>{formatTime(caption.end)}</span>
                        </div>
                        <p className="text-white text-sm">{caption.text}</p>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Download CTA */}
              <Card className="bg-gradient-to-br from-[#CCFF00]/10 to-[#6E44FF]/10 border-white/10 p-6 rounded-2xl">
                <h3 className="font-heading font-semibold text-white mb-2">
                  {showingClips ? 'Download Clip' : 'Download Video'}
                </h3>
                <p className="text-white/60 text-sm mb-4">
                  {showingClips 
                    ? `Download clip ${currentClipIndex + 1} of ${project?.clips?.length || 0}`
                    : 'Get your processed video in original quality.'
                  }
                </p>
                <Button
                  className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-xl"
                  onClick={handleDownload}
                  data-testid="download-cta-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {showingClips ? 'Download Current Clip' : 'Download Video'}
                </Button>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Preview;
